import React from 'react';

/**
 * Event Readiness Advisor — the organizer-facing, point-in-time gap detector.
 *
 * Given an event's onboarding data (computed live, module-gated by the API),
 * it leads with the single highest-priority GAP as a recommended next action,
 * then lists the remaining gaps as one-click chips that jump straight to the
 * fix. At 100% it collapses into a calm "ready to go" state.
 *
 * This is the customer-side sibling of the operator's onboarding tracker:
 * same deterministic intelligence, aimed at the person who runs the event.
 */

export interface OnboardingItem {
  key: string;
  label: string;
  done: boolean;
  detail: string;
  path: string;
}
export interface OnboardingData {
  completed: number;
  total: number;
  percent: number;
  items: OnboardingItem[];
}

export function EventReadinessAdvisor({
  data,
  onNavigate,
}: {
  data: OnboardingData;
  onNavigate: (path: string) => void;
}) {
  const gaps = data.items.filter((i) => !i.done);
  const next = gaps[0];
  const ready = gaps.length === 0;

  return (
    <div style={styles.wrap}>
      <div style={styles.head}>
        <div style={styles.headLeft}>
          <span style={styles.sparkle}>✨</span>
          <div>
            <div style={styles.title}>Readiness Advisor</div>
            <div style={styles.sub}>
              {ready ? 'This event is ready to go.' : `${data.completed} of ${data.total} steps done · ${gaps.length} to go`}
            </div>
          </div>
        </div>
        <div style={styles.ring} aria-label={`${data.percent}% ready`}>
          <svg width="52" height="52" viewBox="0 0 52 52">
            <circle cx="26" cy="26" r="22" fill="none" stroke="#e8eaf0" strokeWidth="6" />
            <circle
              cx="26" cy="26" r="22" fill="none"
              stroke={ready ? '#10b981' : '#1a6b3c'}
              strokeWidth="6" strokeLinecap="round"
              strokeDasharray={`${(data.percent / 100) * (2 * Math.PI * 22)} ${2 * Math.PI * 22}`}
              transform="rotate(-90 26 26)"
            />
          </svg>
          <span style={styles.ringPct}>{data.percent}%</span>
        </div>
      </div>

      {ready ? (
        <div style={styles.readyBox}>🎉 All set — nothing left to configure. Publish when you’re ready.</div>
      ) : (
        <>
          {/* Recommended next action */}
          <button style={styles.recommend} onClick={() => onNavigate(next.path)}>
            <span style={styles.recommendTag}>Do next</span>
            <span style={styles.recommendBody}>
              <span style={styles.recommendLabel}>{next.label}</span>
              <span style={styles.recommendHint}>{next.detail}</span>
            </span>
            <span style={styles.recommendArrow}>→</span>
          </button>

          {/* Remaining gaps as one-click chips */}
          {gaps.length > 1 && (
            <div style={styles.gapsRow}>
              <span style={styles.gapsLabel}>Also pending:</span>
              {gaps.slice(1).map((g) => (
                <button key={g.key} style={styles.gapChip} onClick={() => onNavigate(g.path)} title={g.detail}>
                  {g.label}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

const GREEN = '#1a6b3c';
const styles: Record<string, React.CSSProperties> = {
  wrap: { background: 'linear-gradient(135deg, #ffffff, #f3faf5)', border: '1px solid #d7e8dd', borderRadius: 16, padding: 18, marginBottom: 20, boxShadow: '0 1px 3px rgba(16,24,40,0.04)' },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
  headLeft: { display: 'flex', alignItems: 'center', gap: 12 },
  sparkle: { fontSize: '1.5rem' },
  title: { fontSize: '1.05rem', fontWeight: 800, color: '#0f2d1f' },
  sub: { fontSize: '0.82rem', color: '#5b7366', marginTop: 2 },
  ring: { position: 'relative', width: 52, height: 52, flexShrink: 0 },
  ringPct: { position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', fontSize: '0.72rem', fontWeight: 800, color: '#0f2d1f' },
  readyBox: { marginTop: 14, background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 10, padding: '12px 14px', color: '#065f46', fontSize: '0.88rem', fontWeight: 600 },
  recommend: { display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', marginTop: 14, background: '#fff', border: `1.5px solid ${GREEN}`, borderRadius: 12, padding: '12px 14px', cursor: 'pointer', boxShadow: '0 4px 14px -8px rgba(26,107,60,0.4)' },
  recommendTag: { flexShrink: 0, background: GREEN, color: '#fff', borderRadius: 999, padding: '4px 10px', fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em' },
  recommendBody: { display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 },
  recommendLabel: { fontWeight: 800, color: '#0f2d1f', fontSize: '0.95rem' },
  recommendHint: { fontSize: '0.78rem', color: '#5b7366', marginTop: 1 },
  recommendArrow: { color: GREEN, fontWeight: 800, fontSize: '1.2rem' },
  gapsRow: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 12 },
  gapsLabel: { fontSize: '0.76rem', color: '#5b7366', fontWeight: 700 },
  gapChip: { background: '#fff', color: '#1a6b3c', border: '1px solid #c3dccb', borderRadius: 999, padding: '5px 12px', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' },
};
